import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader2, Trash2, Share2 } from 'lucide-react';
import FabricCanvas from '../components/builder/FabricCanvas';
import ToolPanel from '../components/builder/ToolPanel';
import AnimationPanel from '../components/builder/AnimationPanel';

const API = '/api';

const PLATFORM_SIZES = {
  linkedin: { w: 1200, h: 627, label: 'LinkedIn Banner', display: '1200×627' },
  twitter: { w: 1200, h: 675, label: 'Twitter/X Banner', display: '1200×675' },
  instagram: { w: 1080, h: 1080, label: 'Instagram Post', display: '1080×1080' },
  facebook: { w: 1200, h: 630, label: 'Facebook Banner', display: '1200×630' },
  youtube: { w: 2560, h: 1440, label: 'YouTube Banner', display: '2560×1440' },
};

const DISPLAY_SCALE = 0.38;

export default function SocialBuilder() {
  const canvasRef = useRef(null);
  const [platform, setPlatform] = useState('linkedin');
  const [animation, setAnimation] = useState('none');
  const [bannerName, setBannerName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [myBanners, setMyBanners] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const size = PLATFORM_SIZES[platform];
  const displayW = Math.round(size.w * DISPLAY_SCALE);
  const displayH = Math.round(size.h * DISPLAY_SCALE);

  useEffect(() => {
    axios.get(`${API}/social/`)
      .then(r => setMyBanners(r.data))
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, []);

  const exportGIF = async () => {
    try {
      const GIF = (await import('gif.js')).default;
      const canvas = canvasRef.current?.getCanvas();
      if (!canvas) {
        alert('Canvas not ready');
        return;
      }
      const gif = new GIF({ workers: 2, quality: 10, width: displayW, height: displayH });
      for (let i = 0; i < 20; i++) {
        gif.addFrame(canvas.lowerCanvasEl, { delay: 100, copy: true });
      }
      gif.on('finished', (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${platform}-banner.gif`;
        a.click();
        URL.revokeObjectURL(url);
      });
      gif.render();
    } catch (e) {
      alert('GIF export failed: ' + e.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const json = canvasRef.current?.getJSON() || {};
      const name = bannerName.trim() || `${size.label}`;

      let res;
      if (selectedId) {
        res = await axios.put(`${API}/social/${selectedId}`, { platform, canvas_json: json, name });
        setMyBanners(b => b.map(x => x.id === selectedId ? res.data : x));
      } else {
        res = await axios.post(`${API}/social/`, { platform, canvas_json: json, name });
        setMyBanners(b => [res.data, ...b]);
        setSelectedId(res.data.id);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadBanner = (banner) => {
    canvasRef.current?.loadJSON(banner.canvas_json);
    setPlatform(banner.platform);
    setBannerName(banner.name || '');
    setSelectedId(banner.id);
  };

  const deleteBanner = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API}/social/${id}`);
      setMyBanners(b => b.filter(x => x.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
        canvasRef.current?.clear();
      }
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Share2 className="w-6 h-6 text-pink-600" />
          <h1 className="text-2xl font-bold text-gray-900">Social Media Animation Builder</h1>
        </div>
        <div className="flex items-center gap-3">
          <input
            value={bannerName}
            onChange={e => setBannerName(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40"
            placeholder="Banner name"
          />
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      {/* Platform selector */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {Object.entries(PLATFORM_SIZES).map(([p, s]) => (
          <button
            key={p}
            onClick={() => { setPlatform(p); setSelectedId(null); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
              platform === p
                ? 'bg-pink-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {p} <span className="text-xs opacity-70">{s.display}</span>
          </button>
        ))}
      </div>

      <div className="flex gap-5">
        {/* Left panel */}
        <div className="w-52 flex-shrink-0 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <ToolPanel canvasRef={canvasRef} onExportGIF={exportGIF} />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <AnimationPanel value={animation} onChange={setAnimation} />
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 min-w-0">
          <div className="bg-gray-200 p-4 rounded-xl flex justify-center overflow-auto">
            <FabricCanvas ref={canvasRef} width={displayW} height={displayH} />
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            Editing at {displayW}×{displayH} · Full export: {size.w}×{size.h}px ({size.label})
          </p>
        </div>

        {/* Saved banners */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Saved Banners</h3>
            {loadingList ? (
              <p className="text-xs text-gray-400">Loading...</p>
            ) : myBanners.length === 0 ? (
              <p className="text-xs text-gray-400 leading-relaxed">No saved banners yet.</p>
            ) : (
              <div className="space-y-1.5">
                {myBanners.map(b => (
                  <div
                    key={b.id}
                    onClick={() => loadBanner(b)}
                    className={`flex items-center justify-between p-2 border rounded-lg cursor-pointer text-xs transition-colors ${
                      selectedId === b.id
                        ? 'border-pink-400 bg-pink-50 text-pink-700'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{b.name || b.platform}</div>
                      <div className="text-gray-400 capitalize">{b.platform}</div>
                    </div>
                    <button
                      onClick={(e) => deleteBanner(b.id, e)}
                      className="text-gray-400 hover:text-red-500 ml-1 flex-shrink-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
